import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { usePortalBrand } from "../contexts/PortalBrandContext";
import { CatalogBrandContext } from "../pages/catalog/catalogBrandContext";
import { GuardFallback } from "./common/GuardFallback";
import { LoadingSpinner } from "./common/LoadingSpinner";
import { CatalogThumbnailProvider } from "../pages/catalog/CatalogImage";
import CatalogBrandBar from "../pages/catalog/CatalogBrandBar";
import { ADMIN_CATALOG_BASE, adminCatalogPath } from "../pages/catalog/catalogPaths";

/**
 * Route guard for the catalog (AI onboarding).
 *
 * Live for GB admins by default (Ari, 2026-09-10). For a BRAND_ADMIN it opens
 * only when the `VITE_CATALOG_BRAND_ENABLED` build flag (the global kill
 * switch; Dockerfile.web declares it) is "true" AND `users.me` says the brand
 * has catalog access: it came through onboarding, or a GB admin turned on its
 * switch (Ari, 2026-10-20; `usePermissions().canUseBrandCatalog`).
 *
 * - BRAND_ADMIN (when the gate allows): renders children; no brandId is
 *   injected, the server resolves their own brand.
 * - ADMIN / SUPER_ADMIN: a visible brand bar ("Select a brand before
 *   beginning") sits above every catalog screen. It shares state with the
 *   navbar's brand switcher (both write `PortalBrandContext`) and provides
 *   the selected brand's `snowflakeIdNum` through `CatalogBrandContext`.
 *   Until a brand is resolved the catalog is NOT rendered — an admin call
 *   without brandId hits the shared 'admin' bucket, which is nobody's catalog.
 * - Everyone else: redirected, same as BrandAdminRoute.
 *
 * Admins operate the catalog inside the ADMIN shell (`/admin/catalog/*`,
 * Ari 2026-09-10, option 1). A brand-shell URL opened by an admin — an old
 * link, a bookmark — is rewritten to its admin twin, path and query intact.
 */
export const CatalogRoute: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const { canUseBrandCatalog, canOperateCatalog, isAdminOrHigher, user } =
    usePermissions();
  const { error, refetch } = useCurrentUser();
  const { selectedBrand, isLoadingBrands, brands } = usePortalBrand();
  const { pathname, search } = useLocation();

  const isAdmin = isAdminOrHigher();
  const brandId = isAdmin ? selectedBrand?.snowflakeIdNum : undefined;
  const ctx = useMemo(() => ({ brandId }), [brandId]);

  if (!user) {
    return <GuardFallback error={error} onRetry={refetch} />;
  }

  // Brand admins: the kill switch AND the server's per-brand catalog access.
  const catalogOpen = canOperateCatalog() || canUseBrandCatalog();

  if (!catalogOpen) {
    return <Navigate to="/reports" replace />;
  }

  if (isAdmin && !pathname.startsWith(ADMIN_CATALOG_BASE)) {
    return <Navigate to={adminCatalogPath(pathname, search)} replace />;
  }

  // A brand user has no brand gate — the server resolves their own brand — but
  // still needs the thumbnail map, so the provider wraps both paths (R8).
  if (!isAdmin) {
    return <CatalogThumbnailProvider>{children}</CatalogThumbnailProvider>;
  }

  if (brandId === undefined) {
    const empty = !isLoadingBrands && brands.length === 0;
    return (
      <>
        <CatalogBrandBar />
        <div data-testid="catalog-brand-gate" className="py-12">
          {empty ? (
            <p className="text-center text-sm text-gray-500">
              No brands to work on yet.
            </p>
          ) : isLoadingBrands ? (
            <LoadingSpinner text="Loading brands…" />
          ) : (
            <p className="text-center text-sm text-gray-500">
              Choose a brand above to open its catalog.
            </p>
          )}
        </div>
      </>
    );
  }

  return (
    <CatalogBrandContext.Provider value={ctx}>
      <CatalogBrandBar />
      {/* Keyed on the brand (audit §2 defect 5, 2026-09-01). Every catalog
          screen seeds local state on mount — the readiness tab and selected
          family, the changes-edit map, the accepted-suggestion set, the per-SKU
          done set, the shipment selection, the per-family quantity and customs
          drafts. Without a key those all survived a brand switch and were shown
          against the next brand's data. Remounting also drops the previous
          brand's thumbnail map, which is what the provider holds.
          `children` is a single element here, so the key belongs on the wrapper
          rather than on an <Outlet> — this route renders children, not an
          outlet (CatalogLayout.tsx:33 owns that). */}
      <CatalogThumbnailProvider key={brandId}>{children}</CatalogThumbnailProvider>
    </CatalogBrandContext.Provider>
  );
};

export default CatalogRoute;
